import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle, Zap, ArrowLeft } from 'lucide-react';
import { normalizeApiError } from '../utils/normalizeApiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/\d/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const passwordErrors = password ? validatePassword(password) : [];
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token || !email) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (passwordErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          token,
          new_password: password,
          confirm_password: confirmPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(normalizeApiError(data));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // No token or email provided
  if (!token || !email) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        padding: 'var(--space-lg)'
      }} data-testid="reset-password-page">
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: 'var(--glass-bg)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)',
          padding: 'var(--space-2xl)',
          backdropFilter: 'blur(20px)',
          textAlign: 'center'
        }}>
          <AlertCircle size={48} style={{ color: 'var(--status-danger)', marginBottom: 'var(--space-lg)' }} />
          <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--space-md)' }}>Invalid Reset Link</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-xl)' }}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link
            to="/forgot-password"
            className="btn btn-primary"
            style={{ width: '100%', padding: 'var(--space-md)', textDecoration: 'none' }}
          >
            Request New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      padding: 'var(--space-lg)'
    }} data-testid="reset-password-page">
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'var(--glass-bg)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-2xl)',
        backdropFilter: 'blur(20px)'
      }}>
        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto var(--space-lg)',
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Zap size={32} style={{ color: 'white' }} />
          </div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-xs)'
          }}>
            Create New Password
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {success 
              ? 'Your password has been reset'
              : 'Enter your new password below'
            }
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--status-danger-bg)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }} data-testid="reset-password-error">
            <AlertCircle size={18} style={{ color: 'var(--status-danger)', flexShrink: 0 }} />
            <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{error}</span>
          </div>
        )}

        {/* Success State */}
        {success ? (
          <div>
            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--status-success-bg)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-xl)',
              textAlign: 'center'
            }} data-testid="reset-password-success">
              <CheckCircle size={48} style={{ color: 'var(--status-success)', marginBottom: 'var(--space-md)' }} />
              <p style={{ color: 'var(--status-success)', fontSize: '0.9375rem', fontWeight: '500' }}>
                Password Reset Successfully!
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: 'var(--space-sm)' }}>
                You can now sign in with your new password.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                fontSize: '1rem',
                fontWeight: '600'
              }}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* New Password Input */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{
                  position: 'absolute',
                  left: 'var(--space-md)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="reset-password-input"
                  style={{
                    width: '100%',
                    padding: 'var(--space-md) calc(var(--space-md) * 2 + 18px) var(--space-md) calc(var(--space-md) * 2 + 18px)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9375rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 'var(--space-md)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Password Requirements */}
              {password && passwordErrors.length > 0 && (
                <div style={{ marginTop: 'var(--space-sm)' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 'var(--space-xs)' }}>
                    Password requires:
                  </p>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: 'var(--space-md)', 
                    color: 'var(--status-danger)',
                    fontSize: '0.75rem'
                  }}>
                    {passwordErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              {password && passwordErrors.length === 0 && (
                <div style={{ 
                  marginTop: 'var(--space-sm)', 
                  color: 'var(--status-success)',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <CheckCircle size={14} />
                  Password meets requirements
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{
                  position: 'absolute',
                  left: 'var(--space-md)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="reset-password-confirm-input"
                  style={{
                    width: '100%',
                    padding: 'var(--space-md) var(--space-md) var(--space-md) calc(var(--space-md) * 2 + 18px)',
                    background: 'var(--bg-secondary)',
                    border: `1px solid ${confirmPassword && !passwordsMatch ? 'var(--status-danger)' : 'var(--glass-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9375rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>
              {confirmPassword && !passwordsMatch && (
                <p style={{ 
                  color: 'var(--status-danger)', 
                  fontSize: '0.75rem', 
                  marginTop: 'var(--space-xs)' 
                }}>
                  Passwords do not match
                </p>
              )}
              {passwordsMatch && (
                <div style={{ 
                  marginTop: 'var(--space-xs)', 
                  color: 'var(--status-success)',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <CheckCircle size={14} />
                  Passwords match
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || passwordErrors.length > 0 || !passwordsMatch}
              className="btn btn-primary"
              data-testid="reset-password-submit-btn"
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: 'var(--space-lg)'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>

            {/* Back to Login */}
            <div style={{ textAlign: 'center' }}>
              <Link
                to="/login"
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}
                data-testid="back-to-login-link"
              >
                <ArrowLeft size={16} />
                Back to Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
